import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTeamLogo, getAssetURL } from "../configs/assets";
import { parseMatchTime } from "../utils/dateUtils";

const MatchCard = ({ match }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState("");

  const isLive = match.status === "live";
  const isCompleted = match.status === "completed";

  const handleClick = () => {
    if (isLive || isCompleted) {
      navigate(`/my-teams?matchId=${match.match_id || match.id}`);
    } else {
      navigate(`/create-team/${match.match_id || match.id}`);
    }
  };

  useEffect(() => {
    if (!match?.start_time) return;

    const updateTimer = () => {
      const now = new Date().getTime();
      const start = parseMatchTime(match).getTime();
      const diff = start - now;

      if (diff <= 0) {
        setTimeLeft("");
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      if (diff > 2 * 60 * 60 * 1000) {
        const d = parseMatchTime(match);
        setTimeLeft(d.toLocaleString("en-IN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }));
      } else {
        const timeString = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
        setTimeLeft(`Starts in ${timeString}`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 60000);

    return () => clearInterval(interval);
  }, [match?.start_time]);

  if (!match) return null;

  const colors = [
    { border: "border-orange-500/30", glow: "bg-orange-500/20", text: "text-orange-400" },
    { border: "border-blue-500/30", glow: "bg-blue-500/20", text: "text-blue-400" },
    { border: "border-pink-500/30", glow: "bg-pink-500/20", text: "text-pink-400" },
    { border: "border-purple-500/30", glow: "bg-purple-500/20", text: "text-purple-400" },
    { border: "border-emerald-500/30", glow: "bg-emerald-500/20", text: "text-emerald-400" },
    { border: "border-yellow-500/30", glow: "bg-yellow-500/20", text: "text-yellow-400" },
  ];

  const colorIndex = (String(match?.match_id || match?.id || "").length + (Number(match?.id || 0))) % (colors?.length || 1);
  const theme = colors[colorIndex] || colors[0];

  return (
    <div
      onClick={handleClick}
      className="relative glass-card rounded-theme p-4 flex flex-col items-center transition-all duration-300 active:scale-[0.97] shadow-theme-md hover-glow group animate-fade-in"
    >
      {/* ⏰ STATUS / TIMER */}
      <div className="w-full flex justify-center mb-4 relative z-10">
        {isLive ? (
          <div className="text-[8px] font-bold px-2 py-0.5 rounded-full bg-red-500 text-white animate-pulse uppercase tracking-wider">
            LIVE
          </div>
        ) : isCompleted ? (
          <div className="text-[8px] font-bold px-2 py-0.5 rounded-full bg-theme-bg/50 border border-theme-border text-theme-text-sec uppercase tracking-wider">
            Completed
          </div>
        ) : (
          <div className="text-[8px] font-bold px-2.5 py-1 rounded-full bg-theme-bg/50 border border-theme-border text-theme-text uppercase tracking-widest">
            {timeLeft || "Upcoming"}
          </div>
        )}
      </div>

      {/* 🛡️ TEAM VISUALIZATION */}
      <div className="flex justify-between items-center w-full relative z-10 mb-4 px-1">
        {/* Team 1 */}
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0 animate-float-subtle">
          <div className="w-12 h-12 rounded-2xl overflow-hidden bg-theme-bg border border-theme-border shadow-sm flex items-center justify-center p-2 group-hover:scale-110 transition-transform">
            <img
              src={getTeamLogo(match?.team1, match?.team1_logo)}
              alt={match?.team1 || "T1"}
              className="w-full h-full object-contain"
              loading="lazy"
              onError={(e) => { e.target.src = getTeamLogo(match?.team1) }}
            />
          </div>
          <span className="text-[11px] font-extrabold text-theme-text uppercase tracking-wider truncate w-full text-center">{match?.team1_short || match?.team1?.substring(0, 3) || "T1"}</span>
        </div>

        {/* ⚔️ VS */}
        <div className="mx-3 shrink-0">
          <div className="w-7 h-7 rounded-full bg-theme-bg border border-theme-border flex items-center justify-center backdrop-blur-sm shadow-inner">
            <span className="text-[9px] font-black text-theme-text-sec italic opacity-50">VS</span>
          </div>
        </div>

        {/* Team 2 */}
        <div className="flex flex-col items-center gap-2 flex-1 min-w-0 animate-float-subtle animate-float-delayed">
          <div className="w-12 h-12 rounded-2xl overflow-hidden bg-theme-bg border border-theme-border shadow-sm flex items-center justify-center p-2 group-hover:scale-110 transition-transform">
            <img
              src={getTeamLogo(match?.team2, match?.team2_logo)}
              alt={match?.team2 || "T2"}
              className="w-full h-full object-contain"
              loading="lazy"
              onError={(e) => { e.target.src = getTeamLogo(match?.team2) }}
            />
          </div>
          <span className="text-[11px] font-extrabold text-theme-text uppercase tracking-wider truncate w-full text-center">{match?.team2_short || match?.team2?.substring(0, 3) || "T2"}</span>
        </div>
      </div>

      {/* Lineup badge */}
      {match?.lineup_announced && !isLive && !isCompleted && (
        <div className="flex items-center gap-1.5 bg-accent/10 border border-accent/20 px-3 py-1 rounded-full mb-3 shadow-[0_0_10px_rgba(99,102,241,0.1)]">
          <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse"></span>
          <span className="text-[8px] font-black text-accent uppercase tracking-[0.15em]">Lineups Out</span>
        </div>
      )}

      {/* 💰 FOOTER */}
      <div className="self-stretch mt-auto bg-theme-bg/30 border-t border-theme-border/50 py-2.5 flex justify-center items-center relative z-10 -mx-4 -mb-4 rounded-b-theme backdrop-blur-sm">
        <span className="text-[9px] text-theme-text-sec font-bold uppercase tracking-[0.2em]">{match?.format || match?.short_title || "T20"}</span>
      </div>
    </div>
  );
};

export default MatchCard;